import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Terminal, ShieldAlert, KeyRound } from "lucide-react";

/**
 * Hidden Operator Console Login.
 * Reached via secret URL /nvr-console — not linked from anywhere.
 * Only operator + super_admin credentials are accepted here.
 */
export default function OperatorLogin() {
  const { operatorLogin, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    server_ip: "10.0.0.1",
    port: "22",
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && (user.role === "operator" || user.role === "super_admin")) {
      navigate("/nvr-console/panel", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await operatorLogin(form);
      navigate("/nvr-console/panel");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center relative overflow-hidden">
      {/* Ambient CRT background */}
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{
             backgroundImage:
               "radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.08) 0%, transparent 60%)",
           }}
      />
      <div className="absolute inset-0 pointer-events-none"
           style={{
             backgroundImage:
               "repeating-linear-gradient(to bottom, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 4px)",
           }}
      />

      <div className="relative w-full max-w-[440px] border border-amber-500/40 bg-black">
        <div className="bg-amber-500 text-black px-3 py-1 flex items-center justify-between">
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest font-bold">
            <Terminal size={14} />
            System Config Console
          </div>
          <div className="mono text-[10px]">Operator</div>
        </div>

        <form onSubmit={onSubmit} className="p-6 flex flex-col gap-4">
          <div className="mono text-[10px] text-amber-500/80 uppercase tracking-widest">
            $ operator sign-in
          </div>
          <div className="mono text-[11px] text-zinc-400 leading-relaxed border-l-2 border-amber-500/50 pl-3">
            Maintenance interface for NVR content management. Sign in with operator credentials
            provided by your organization administrator.
          </div>

          <div className="grid grid-cols-[2fr_1fr] gap-3 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Host</Label>
              <Input
                data-testid="op-server-ip"
                className="mono bg-zinc-950 border-zinc-800 focus:border-amber-500 rounded-none mt-1"
                value={form.server_ip}
                onChange={(e) => setForm({ ...form, server_ip: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Port</Label>
              <Input
                data-testid="op-port"
                className="mono bg-zinc-950 border-zinc-800 focus:border-amber-500 rounded-none mt-1"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Operator ID</Label>
            <Input
              data-testid="op-username"
              className="mono bg-zinc-950 border-zinc-800 focus:border-amber-500 rounded-none mt-1"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              autoComplete="off"
              required
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Passphrase</Label>
            <Input
              data-testid="op-password"
              type="password"
              className="mono bg-zinc-950 border-zinc-800 focus:border-amber-500 rounded-none mt-1"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="off"
              required
            />
          </div>

          {error && (
            <div data-testid="op-error" className="flex items-center gap-2 border border-red-900/60 bg-red-950/30 text-red-400 text-xs px-3 py-2">
              <ShieldAlert size={14} />
              <span className="mono">{error}</span>
            </div>
          )}

          <Button
            type="submit"
            data-testid="op-submit"
            disabled={busy}
            className="rounded-none bg-amber-500 hover:bg-amber-400 text-black font-bold tracking-widest uppercase text-xs h-11"
          >
            <KeyRound size={13} className="mr-2" />
            {busy ? "Authenticating..." : "▸ Grant Access"}
          </Button>

          <div className="mono text-[10px] text-zinc-600 border-t border-zinc-900 pt-3 flex items-center justify-between">
            <span>&gt; session encrypted · tail /var/log/access</span>
            <img src="/logo.png" alt="Cam Control Hub" style={{ height: 14 }} className="object-contain opacity-60" />
          </div>
        </form>
      </div>
    </div>
  );
}
